import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnnouncer } from '../../layout/Announcer';
import { TOAST_DURATION, toast, useToasts, type Toast } from './toasts';

/**
 * The toasts of the app (D35): a labelled region in the bottom corner, newest last. A toast never takes focus; it is
 * announced politely (the page shows errors itself, focused, in its error summary or banner). The time stops while
 * the pointer is over the toasts, focus is in them or the browser tab is hidden; "Zavrieť" or Escape closes a toast
 * at once (Escape the newest one, unless a dialog is open).
 */
export function Toaster() {
  const { t } = useTranslation();
  const { announce } = useAnnouncer();
  const toasts = useToasts();
  const region = useRef<HTMLElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.visibilityState === 'hidden');
  const announced = useRef(0);

  // every new toast once, in the order shown
  useEffect(() => {
    for (const item of toasts) {
      if (item.id > announced.current) {
        announced.current = item.id;
        announce(`${item.type === 'error' ? t('toast.error') : t('toast.success')}: ${item.text}`);
      }
    }
  }, [toasts, announce, t]);

  useEffect(() => {
    const onVisibility = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const close = useCallback((id: number, viaButton: boolean) => {
    if (viaButton && region.current?.contains(document.activeElement)) {
      // focus stays in the toasts while there are others, else returns to the page
      const buttons = [...(region.current?.querySelectorAll<HTMLButtonElement>('.app-toast__close') ?? [])];
      const index = buttons.findIndex((b) => b.dataset.toast === String(id));
      const next = buttons[index + 1] ?? buttons[index - 1];
      if (next) next.focus();
      else document.getElementById('main-content')?.focus({ preventScroll: true });
    }
    toast.dismiss(id);
  }, []);

  useEffect(() => {
    const newest = toasts[toasts.length - 1];
    if (!newest) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('dialog[open]')) return;
      close(newest.id, true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toasts, close]);

  // the region goes away with its last toast: no mouseleave / blur follows (state adjusted during render)
  const empty = toasts.length === 0;
  const [wasEmpty, setWasEmpty] = useState(empty);
  if (wasEmpty !== empty) {
    setWasEmpty(empty);
    setHovered(false);
    setFocused(false);
  }

  if (empty) return null;
  const paused = hovered || focused || hidden;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- hover only pauses the time; focus does the same for keyboard users
    <section
      ref={region}
      className="app-toasts"
      aria-label={t('toast.region')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} item={item} paused={paused} onClose={close} />
      ))}
    </section>
  );
}

function ToastItem({ item, paused, onClose }: { item: Toast; paused: boolean; onClose: (id: number, viaButton: boolean) => void }) {
  const { t } = useTranslation();
  // time left; counted down only while not paused
  const remaining = useRef(TOAST_DURATION[item.type]);
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => onClose(item.id, false), Math.max(remaining.current, 0));
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [paused, item.id, onClose]);

  const title = item.type === 'error' ? t('toast.error') : t('toast.success');
  return (
    <div className={`app-toast app-toast--${item.type}`} data-testid="toast">
      <span className="app-toast__icon" aria-hidden="true">
        {item.type === 'error' ? '!' : '✓'}
      </span>
      <p className="app-toast__body">
        <strong className="app-toast__title">{title}</strong> {item.text}
      </p>
      <button type="button" className="app-toast__close" data-toast={item.id} onClick={() => onClose(item.id, true)}>
        <span aria-hidden="true">×</span>
        <span className="govuk-visually-hidden">{t('toast.close')}</span>
      </button>
    </div>
  );
}
